import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, doc, getDoc, deleteDoc, updateDoc, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import './GestionPrecios.css';
import FloatingMenu from '../FloatingMenu';

const GestionPrecios = () => {
  const [rooms, setRooms] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Estado para el formulario de creación/edición de temporada
  const [currentSeason, setCurrentSeason] = useState({
    nombre: '',
    fechaInicio: '',
    fechaFin: '',
    precios: {} // { roomId: { 1: precio, 2: precio, ... } }
  });

  // Estado para controlar la creación o edición 
  const [isEditing, setIsEditing] = useState(false);

  // Cargar habitaciones y temporadas al inicio
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Cargar habitaciones desde Firestore
        const habitacionesCollection = collection(db, 'habitaciones');
        const habitacionesSnapshot = await getDocs(habitacionesCollection);
        
        if (habitacionesSnapshot.docs.length > 0) {
          const roomsList = habitacionesSnapshot.docs.map(doc => ({
            id: doc.id.toString(),
            nombre: doc.data().nombre || 'Sin nombre',
            tipo: doc.data().tipo || 'Estándar',
            capacidad: doc.data().capacity || 1,
            precio: doc.data().precio || 0,
            preciosPorOcupacion: doc.data().preciosPorOcupacion || {}
          }));
          
          // Ordenar habitaciones por ID
          roomsList.sort((a, b) => {
            const idA = parseInt(a.id);
            const idB = parseInt(b.id);
            return idA - idB;
          });
          
          setRooms(roomsList);
          
          // Inicializar precios vacíos para todas las habitaciones y sus capacidades
          const initialPrices = {};
          roomsList.forEach(room => {
            initialPrices[room.id] = {};
            for (let i = 1; i <= room.capacidad; i++) {
              initialPrices[room.id][i] = 0;
            }
          });
          
          setCurrentSeason(prev => ({
            ...prev,
            precios: initialPrices
          }));
        }
        
        // Cargar temporadas desde Firestore
        await fetchSeasons();
        
        setLoading(false);
      } catch (err) {
        console.error('Error al cargar datos:', err);
        setError('Error al cargar datos: ' + err.message);
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // Función para cargar temporadas
  const fetchSeasons = async () => {
    try {
      const seasonsCollection = collection(db, 'temporadas');
      const seasonsQuery = query(seasonsCollection, orderBy('fechaInicio', 'desc'));
      const seasonsSnapshot = await getDocs(seasonsQuery);
      
      if (seasonsSnapshot.docs.length > 0) {
        const seasonsList = seasonsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setSeasons(seasonsList);
      } else {
        setSeasons([]);
      }
    } catch (err) {
      console.error('Error al cargar temporadas:', err);
      setError('Error al cargar temporadas: ' + err.message);
    }
  };

  // Manejar cambios en el formulario de temporada
  const handleSeasonChange = (e) => {
    const { name, value } = e.target;
    setCurrentSeason(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Manejar cambios en los precios
  const handlePriceChange = (roomId, ocupacion, value) => {
    const precio = value === '' ? 0 : parseInt(value);
    
    setCurrentSeason(prev => {
      // Crear copias para evitar modificar el estado directamente
      const newPrecios = { ...prev.precios };
      
      if (!newPrecios[roomId]) {
        newPrecios[roomId] = {};
      }
      
      newPrecios[roomId] = {
        ...newPrecios[roomId],
        [ocupacion]: precio
      };
      
      return {
        ...prev,
        precios: newPrecios
      };
    });
  };

  // Guardar temporada
  const handleSaveSeason = async (e) => {
    e.preventDefault();
    
    if (!currentSeason.nombre.trim()) {
      setError('Por favor, ingrese un nombre para la temporada');
      return;
    }
    
    if (!currentSeason.fechaInicio || !currentSeason.fechaFin) {
      setError('Por favor, seleccione las fechas de inicio y fin');
      return;
    }
    
    // Verificar que la fecha de fin sea posterior a la de inicio
    const fechaInicio = new Date(currentSeason.fechaInicio);
    const fechaFin = new Date(currentSeason.fechaFin);
    
    if (fechaFin <= fechaInicio) {
      setError('La fecha de fin debe ser posterior a la fecha de inicio');
      return;
    }
    
    try {
      setIsSaving(true);
      
      // Objeto de datos para Firestore
      const seasonData = {
        nombre: currentSeason.nombre,
        fechaInicio: fechaInicio,
        fechaFin: fechaFin,
        precios: currentSeason.precios,
        createdAt: new Date()
      };
      
      if (isEditing && currentSeason.id) {
        // Actualizar temporada existente
        await updateDoc(doc(db, 'temporadas', currentSeason.id), seasonData);
        setSuccess('Temporada actualizada correctamente');
      } else {
        // Crear nueva temporada
        await addDoc(collection(db, 'temporadas'), seasonData);
        setSuccess('Temporada creada correctamente');
      }
      
      // Recargar temporadas
      await fetchSeasons();
      
      // Reiniciar formulario y cerrar modal
      resetForm();
      setIsModalOpen(false);
    } catch (err) {
      console.error('Error al guardar temporada:', err);
      setError('Error al guardar temporada: ' + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  // Editar temporada existente
  const handleEditSeason = async (seasonId) => {
    try {
      setLoading(true);
      
      const seasonDoc = await getDoc(doc(db, 'temporadas', seasonId));
      
      if (seasonDoc.exists()) {
        const seasonData = seasonDoc.data();
        
        // Convertir las fechas a formato de fecha HTML (YYYY-MM-DD)
        const fechaInicio = seasonData.fechaInicio.toDate ? 
          seasonData.fechaInicio.toDate().toISOString().split('T')[0] : 
          new Date(seasonData.fechaInicio).toISOString().split('T')[0];
          
        const fechaFin = seasonData.fechaFin.toDate ? 
          seasonData.fechaFin.toDate().toISOString().split('T')[0] : 
          new Date(seasonData.fechaFin).toISOString().split('T')[0];
        
        // Inicializar precios para todas las habitaciones y capacidades
        const completePrecios = { ...seasonData.precios };
        
        // Asegurarse de que existan todos los precios para todas las habitaciones
        rooms.forEach(room => {
          if (!completePrecios[room.id]) {
            completePrecios[room.id] = {};
          }
          
          for (let i = 1; i <= room.capacidad; i++) {
            if (completePrecios[room.id][i] === undefined) {
              completePrecios[room.id][i] = room.preciosPorOcupacion[i] || room.precio;
            }
          }
        });
        
        setCurrentSeason({
          id: seasonId,
          nombre: seasonData.nombre,
          fechaInicio: fechaInicio,
          fechaFin: fechaFin,
          precios: completePrecios
        });
        
        setIsEditing(true);
        setIsModalOpen(true);
      } else {
        setError('No se encontró la temporada');
      }
    } catch (err) {
      console.error('Error al cargar temporada:', err);
      setError('Error al cargar temporada: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Eliminar temporada
  const handleDeleteSeason = async (seasonId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta temporada? Esta acción no se puede deshacer.')) {
      return;
    }
    
    try {
      setLoading(true);
      await deleteDoc(doc(db, 'temporadas', seasonId));
      
      // Recargar temporadas
      await fetchSeasons();
      
      setSuccess('Temporada eliminada correctamente');
    } catch (err) {
      console.error('Error al eliminar temporada:', err);
      setError('Error al eliminar temporada: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Crear nueva temporada
  const handleNewSeason = () => {
    // Inicializar precios para la temporada
    const initialPrices = {};
    rooms.forEach(room => {
      initialPrices[room.id] = {};
      for (let i = 1; i <= room.capacidad; i++) {
        initialPrices[room.id][i] = room.preciosPorOcupacion[i] || room.precio;
      }
    });
    
    // Reiniciar el formulario
    setCurrentSeason({
      nombre: '',
      fechaInicio: '',
      fechaFin: '',
      precios: initialPrices
    });
    
    setIsEditing(false);
    setIsModalOpen(true);
  };

  // Resetear formulario
  const resetForm = () => {
    // Inicializar precios vacíos
    const initialPrices = {};
    rooms.forEach(room => {
      initialPrices[room.id] = {};
      for (let i = 1; i <= room.capacidad; i++) {
        initialPrices[room.id][i] = room.preciosPorOcupacion[i] || room.precio;
      }
    });
    
    setCurrentSeason({
      nombre: '',
      fechaInicio: '',
      fechaFin: '',
      precios: initialPrices
    });
    
    setIsEditing(false);
  };

  // Cerrar mensajes
  const handleCloseMessage = () => {
    setError(null);
    setSuccess(null);
  };

  // Formatear fecha
  const formatDate = (date) => {
    if (!date) return '';
    
    let formattedDate;
    if (date.toDate) { // Firestore Timestamp
      formattedDate = date.toDate();
    } else if (date instanceof Date) {
      formattedDate = date;
    } else {
      formattedDate = new Date(date);
    }
    
    return formattedDate.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div className="gestion-precios">
      <h1>Gestión de Precios por Temporada</h1>
      
      {/* Mensajes de éxito/error */}
      {error && (
        <div className="message error">
          {error}
          <button onClick={handleCloseMessage}>&times;</button>
        </div>
      )}
      
      {success && (
        <div className="message success">
          {success}
          <button onClick={handleCloseMessage}>&times;</button>
        </div>
      )}
      
      {/* Botón para crear nueva temporada */}
      <div className="season-actions">
        <button className="btn-primary" onClick={handleNewSeason}>
          Crear Nueva Temporada
        </button>
      </div>
      
      {/* Lista de temporadas */}
      <div className="seasons-list">
        <h2>Temporadas Existentes</h2>
        {loading ? (
          <p>Cargando temporadas...</p>
        ) : seasons.length === 0 ? (
          <p>No hay temporadas configuradas. Crea una nueva temporada para comenzar.</p>
        ) : (
          <div className="table-responsive">
            <table className="seasons-table">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Fecha Inicio</th>
                  <th>Fecha Fin</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {seasons.map(season => (
                  <tr key={season.id}>
                    <td>{season.nombre}</td>
                    <td>{formatDate(season.fechaInicio)}</td>
                    <td>{formatDate(season.fechaFin)}</td>
                    <td>
                      <button 
                        className="btn-edit"
                        onClick={() => handleEditSeason(season.id)}
                      >
                        Editar
                      </button>
                      <button 
                        className="btn-delete"
                        onClick={() => handleDeleteSeason(season.id)}
                      >
                        Eliminar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      {/* Modal para crear/editar temporada */}
      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content season-modal">
            <h2>{isEditing ? 'Editar Temporada' : 'Crear Nueva Temporada'}</h2>
            
            <form onSubmit={handleSaveSeason}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="nombre">Nombre de la Temporada</label>
                  <input
                    type="text"
                    id="nombre"
                    name="nombre"
                    value={currentSeason.nombre}
                    onChange={handleSeasonChange}
                    placeholder="Ej: Temporada Alta 2023"
                    required
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="fechaInicio">Fecha de Inicio</label>
                  <input
                    type="date"
                    id="fechaInicio"
                    name="fechaInicio"
                    value={currentSeason.fechaInicio}
                    onChange={handleSeasonChange}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="fechaFin">Fecha de Fin</label>
                  <input
                    type="date"
                    id="fechaFin"
                    name="fechaFin"
                    value={currentSeason.fechaFin}
                    onChange={handleSeasonChange}
                    required
                  />
                </div>
              </div>
              
              <h3>Precios por Habitación y Ocupación</h3>
              
              <div className="rooms-prices">
                {rooms.map(room => (
                  <div key={room.id} className="room-price-card">
                    <h4>Habitación {room.id} - {room.nombre}</h4>
                    <p className="room-capacity">Capacidad: {room.capacidad} personas</p>
                    
                    <div className="price-inputs">
                      {Array.from({ length: room.capacidad }, (_, i) => i + 1).map(ocupacion => (
                        <div key={ocupacion} className="price-input-group">
                          <label htmlFor={`price-${room.id}-${ocupacion}`}>
                            Precio para {ocupacion} {ocupacion === 1 ? 'persona' : 'personas'}
                          </label>
                          <div className="price-input-wrapper">
                            <span className="price-currency">$</span>
                            <input
                              type="number"
                              id={`price-${room.id}-${ocupacion}`}
                              value={currentSeason.precios[room.id]?.[ocupacion] || 0}
                              onChange={(e) => handlePriceChange(room.id, ocupacion, e.target.value)}
                              min="0"
                              required
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="form-actions">
                <button 
                  type="button" 
                  className="btn-cancel"
                  onClick={() => setIsModalOpen(false)}
                >
                  Cancelar
                </button>
                
                <button 
                  type="submit" 
                  className="btn-save"
                  disabled={isSaving}
                >
                  {isSaving ? 'Guardando...' : isEditing ? 'Actualizar' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      <FloatingMenu />
    </div>
  );
};

export default GestionPrecios; 